import { ProductInventoryUnit } from '@gooduncles/gu-app-schema';
import { Select } from 'antd';
import { ColDef, ColGroupDef } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { FC, useMemo, useState } from 'react';

import { formatNumberForAgCell, uomUnitCellStyle } from 'src/utils/ag-grid-util';
import { cellValidatorForInventoryUnit, dailyOutboundTransactionTableHandlers } from 'src/utils/product-inventory-util';
import { ProcessedOutboundTransaction } from 'src/utils/transaction-util';

const costEditorCellStyle = (params: any) =>
  params.node?.rowPinned || cellValidatorForInventoryUnit(params) ? {} : { backgroundColor: 'var(--gray200)' };

const isRowSelectable = (params: any) => {
  const { ea, bundle, inventory } = params.data ?? {};
  return (
    // 신규 입력값이 있고 현재 매입가와 다른 경우
    (ea?.cost !== null && ea?.cost !== undefined && ea?.cost !== inventory?.uom.ea?.cost) ||
    (bundle?.cost !== null && bundle?.cost !== undefined && bundle?.cost !== inventory?.uom.bundle?.cost)
  );
};

/** 발주량(EA 기준)에 곱할 현재 매입 단가. ea 우선, 없으면 bundle 단가를 ea로 환산 */
const getCurrentEaCost = (inventory: ProcessedOutboundTransaction['inventory']): number => {
  const ea = inventory?.uom.ea;
  if (ea?.cost != null) return ea.cost;
  const bundle = inventory?.uom.bundle;
  if (bundle?.cost != null && bundle.ratio > 0) return bundle.cost / bundle.ratio;
  return 0;
};

const getTotalAmount = (row: ProcessedOutboundTransaction | undefined): number =>
  row ? Math.round(getCurrentEaCost(row.inventory) * row.purchaseQuantity) : 0;

/** 발주량(EA 기준)에 곱할 신규 매입 단가. 신규 ea 우선, 없으면 신규 bundle 환산, 둘 다 없으면 현재 단가 */
const getNewEaCost = (row: ProcessedOutboundTransaction): number => {
  if (row.ea?.cost != null) return row.ea.cost;
  const bundleRatio = row.inventory?.uom.bundle?.ratio;
  if (row.bundle?.cost != null && bundleRatio != null && bundleRatio > 0) return row.bundle.cost / bundleRatio;
  return getCurrentEaCost(row.inventory);
};

const getNewTotalAmount = (row: ProcessedOutboundTransaction | undefined): number =>
  row ? Math.round(getNewEaCost(row) * row.purchaseQuantity) : 0;

interface PurchaseManagementTableProps {
  date: string;
  rowData: ProcessedOutboundTransaction[];
  setSelectedRows: React.Dispatch<React.SetStateAction<ProcessedOutboundTransaction[]>>;
}

const defaultColDef: ColDef<ProcessedOutboundTransaction> = {
  sortable: true,
  resizable: true,
  filter: true,
};

const PurchaseManagementTable: FC<PurchaseManagementTableProps> = ({ date, rowData, setSelectedRows }) => {
  const [selectedSupplierId, setSelectedSupplierId] = useState<string | null>(null);

  const supplierOptions = useMemo(() => {
    const map = new Map<string, { id: string; name: string; groupId: string }>();
    rowData.forEach((row) => {
      const supplier = row.productSupplier;
      if (supplier && !map.has(supplier._id)) {
        map.set(supplier._id, { id: supplier._id, name: supplier.name, groupId: supplier.groupId ?? '' });
      }
    });
    return Array.from(map.values())
      .sort((a, b) => a.groupId.localeCompare(b.groupId) || a.name.localeCompare(b.name))
      .map((supplier) => ({
        value: supplier.id,
        label: supplier.groupId ? `[${supplier.groupId}] ${supplier.name}` : supplier.name,
      }));
  }, [rowData]);

  const filteredRowData = useMemo(
    () => (selectedSupplierId ? rowData.filter((row) => row.productSupplier?._id === selectedSupplierId) : []),
    [rowData, selectedSupplierId],
  );

  const pinnedBottomRowData = useMemo(() => {
    if (filteredRowData.length === 0) return [];
    const summary = filteredRowData.reduce(
      (acc, row) => {
        acc.totalQuantity += row.totalQuantity;
        acc.purchaseQuantity += row.purchaseQuantity;
        acc.totalAmount += getTotalAmount(row);
        acc.newTotalAmount += getNewTotalAmount(row);
        acc.diffAmount += getNewTotalAmount(row) - getTotalAmount(row);
        return acc;
      },
      { totalQuantity: 0, purchaseQuantity: 0, totalAmount: 0, newTotalAmount: 0, diffAmount: 0 },
    );
    return [{ isSummary: true, ...summary }];
  }, [filteredRowData]);

  const columnDefs: (ColDef<ProcessedOutboundTransaction> | ColGroupDef<ProcessedOutboundTransaction>)[] = useMemo(
    () => [
      {
        headerName: '재고ID',
        field: 'inventoryId',
        hide: true,
      },
      {
        headerName: '',
        field: 'selection',
        width: 54,
        headerCheckboxSelection: true,
        headerCheckboxSelectionFilteredOnly: true,
        checkboxSelection: true,
      },
      {
        headerName: '재고명',
        field: 'inventory.inventoryName',
        width: 260,
        wrapText: true,
        autoHeight: true,
        valueGetter: (params) => (params.node?.rowPinned ? '합계' : params.data?.inventory?.inventoryName),
        cellStyle: (params) => ({
          lineHeight: '1.1em',
          display: 'flex',
          alignItems: 'center',
          letterSpacing: '-0.025em',
          wordBreak: 'keep-all',
          fontWeight: params.node?.rowPinned ? 700 : 'inherit',
          color: params.data?.isPreOrder ? '#ff1493' : 'inherit',
        }),
      },
      {
        headerName: '필요',
        field: 'totalQuantity',
        width: 100,
        filter: 'agNumberColumnFilter',
        cellStyle: (params) => (params.node?.rowPinned ? null : { backgroundColor: '#ffff00' }),
      },
      {
        headerName: '발주',
        field: 'purchaseQuantity',
        width: 100,
        filter: 'agNumberColumnFilter',
        cellStyle: (params) => {
          if (params.node?.rowPinned) return null;
          return params.value > 0 ? { backgroundColor: '#b6e1cd' } : null;
        },
      },
      {
        headerName: '거래처',
        field: 'productSupplier.name',
        width: 130,
      },
      {
        headerName: '매입가 ea',
        children: [
          {
            headerName: '현재',
            field: 'inventory.uom.ea.cost',
            width: 100,
            type: 'numericColumn',
            valueFormatter: formatNumberForAgCell,
            cellStyle: uomUnitCellStyle,
          },
          {
            headerName: '*신규',
            field: 'ea.cost',
            width: 100,
            editable: (params) => !params.node?.rowPinned,
            type: 'numericColumn',
            cellStyle: costEditorCellStyle,
            valueFormatter: formatNumberForAgCell,
            valueSetter: dailyOutboundTransactionTableHandlers.cost.valueSetter,
            onCellValueChanged: (params) =>
              dailyOutboundTransactionTableHandlers.cost.onCellValueChanged(params, ProductInventoryUnit.EA, date),
          },
        ],
      },
      {
        headerName: '매입가 bundle',
        children: [
          {
            headerName: '현재',
            field: 'inventory.uom.bundle.cost',
            width: 100,
            type: 'numericColumn',
            valueFormatter: formatNumberForAgCell,
            cellStyle: uomUnitCellStyle,
          },
          {
            headerName: '*신규',
            field: 'bundle.cost',
            width: 100,
            editable: (params) => !params.node?.rowPinned,
            type: 'numericColumn',
            cellStyle: costEditorCellStyle,
            valueFormatter: formatNumberForAgCell,
            valueSetter: dailyOutboundTransactionTableHandlers.cost.valueSetter,
            onCellValueChanged: (params) =>
              dailyOutboundTransactionTableHandlers.cost.onCellValueChanged(params, ProductInventoryUnit.BUNDLE, date),
          },
        ],
      },
      {
        headerName: '총액',
        headerTooltip: '현재 매입가 × 발주량',
        colId: 'totalAmount',
        width: 120,
        type: 'numericColumn',
        valueGetter: (params) =>
          params.node?.rowPinned ? (params.data as any)?.totalAmount : getTotalAmount(params.data),
        valueFormatter: formatNumberForAgCell,
        cellStyle: (params) => (params.node?.rowPinned ? { fontWeight: 700 } : null),
      },
      {
        headerName: '신규 총액',
        headerTooltip: '신규 매입가 × 발주량 (신규 미입력 시 현재 매입가 적용)',
        colId: 'newTotalAmount',
        width: 120,
        type: 'numericColumn',
        valueGetter: (params) =>
          params.node?.rowPinned ? (params.data as any)?.newTotalAmount : getNewTotalAmount(params.data),
        valueFormatter: formatNumberForAgCell,
        cellStyle: (params) => (params.node?.rowPinned ? { fontWeight: 700 } : null),
      },
      {
        headerName: '차이',
        headerTooltip: '신규 총액 - 총액 (- 절감 / + 손해)',
        colId: 'diffAmount',
        width: 120,
        type: 'numericColumn',
        valueGetter: (params) =>
          params.node?.rowPinned
            ? (params.data as any)?.diffAmount
            : getNewTotalAmount(params.data) - getTotalAmount(params.data),
        // 손해(상승) ▲ / 절감(감소) ▼ 화살표로 방향을 표시하고 금액은 절대값으로 보여준다.
        valueFormatter: (params) => {
          const diff = params.value ?? 0;
          if (diff === 0) return formatNumberForAgCell(params);
          const arrow = diff > 0 ? '▲' : '▼';
          return `${arrow} ${formatNumberForAgCell({ ...params, value: Math.abs(diff) })}`;
        },
        cellStyle: (params) => {
          const diff = params.value ?? 0;
          // 우리 입장에서 - 는 절감(좋음, 초록), + 는 손해(나쁨, 빨강)
          const color = diff > 0 ? 'var(--red500)' : diff < 0 ? 'var(--green400)' : 'inherit';
          return { color, fontWeight: params.node?.rowPinned ? 700 : 'inherit' };
        },
      },
      {
        headerName: '증감률',
        headerTooltip: '차이 / 총액 × 100 (- 절감 / + 손해)',
        colId: 'diffRate',
        width: 110,
        type: 'numericColumn',
        valueGetter: (params) => {
          const total = params.node?.rowPinned ? (params.data as any)?.totalAmount ?? 0 : getTotalAmount(params.data);
          if (total === 0) return null;
          const diff = params.node?.rowPinned
            ? (params.data as any)?.diffAmount ?? 0
            : getNewTotalAmount(params.data) - total;
          return (diff / total) * 100;
        },
        // 손해(상승) ▲ / 절감(감소) ▼ 화살표로 방향을 표시하고 절대값으로 보여준다.
        valueFormatter: (params) => {
          const rate = params.value;
          if (rate == null) return '';
          const arrow = rate > 0 ? '▲ ' : rate < 0 ? '▼ ' : '';
          return `${arrow}${formatNumberForAgCell({ ...params, value: Math.abs(rate) }, 1)}%`;
        },
        cellStyle: (params) => {
          const rate = params.value ?? 0;
          const color = rate > 0 ? 'var(--red500)' : rate < 0 ? 'var(--green400)' : 'inherit';
          return { color, fontWeight: params.node?.rowPinned ? 700 : 'inherit' };
        },
      },
    ],
    [date],
  );

  return (
    <div className='flexColumn height100'>
      <div style={{ padding: '8px 0' }}>
        <Select
          showSearch
          allowClear
          placeholder='거래처 선택'
          style={{ width: 280 }}
          value={selectedSupplierId}
          onChange={(value) => setSelectedSupplierId(value ?? null)}
          options={supplierOptions}
          optionFilterProp='label'
        />
      </div>
      <div className='ag-theme-alpine bgWhite' style={{ flex: 1 }}>
        <AgGridReact
          rowData={filteredRowData}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          alwaysShowHorizontalScroll={true}
          getRowId={(params) => params.data.inventoryId}
          pinnedBottomRowData={pinnedBottomRowData}
          onSelectionChanged={(e) => setSelectedRows(e.api.getSelectedRows())}
          // 행 선택
          rowSelection={'multiple'}
          isRowSelectable={isRowSelectable}
          // 셀 값이 변경된 후 해당 행이 선택 가능한지 확인하고, 선택 불가능하면 선택 해제
          onCellValueChanged={(params) => {
            const node = params.node;
            if (node.isSelected()) {
              const isSelectable = isRowSelectable(params);
              if (!isSelectable) {
                node.setSelected(false);
              }
            }
          }}
          // 행 선택을 켜놓더라도 개별 셀의 내용을 복사할 수 있도록한다.
          enableCellTextSelection={true}
        />
      </div>
    </div>
  );
};

export default PurchaseManagementTable;
